import {
  createContext,
  lazy,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
// Type-only import — erased at build time, so react-joyride runtime stays out of this chunk.
import type { Step } from "react-joyride";
import { TOUR_STEPS, hasSeenTour, markTourSeen, type TourId } from "@/lib/tour-steps";
import { isInAppBrowser } from "@/lib/in-app-browser";

/** TourHost pulls in react-joyride (~83KB); load it only when a tour actually runs. */
const TourHost = lazy(() => import("./tour-host"));

/** Độ trễ trước khi tự bật tour cho user mới — chờ DOM & animation ổn định. */
const AUTO_START_DELAY_MS = 900;

/**
 * Cờ master bật/tắt toàn bộ tour.
 * `false` => tour không tự bật, mọi lệnh gọi startTour() bị vô hiệu, nút "Hướng
 * dẫn" tự ẩn (hasActiveTour = false).
 *
 * Khi `true`: tour CHỈ hiện trong 2 trường hợp:
 *   1. Onboarding lần đầu cho mỗi khu vực — do `useRegisterTour` tự bật đúng 1
 *      lần (đánh dấu hasSeenTour ngay), không lặp lại ở các lần sau.
 *   2. Người dùng tự bấm nút "Hướng dẫn".
 */
// TẮT theo quyết định chủ dự án (25/08/2026): không tính năng nào tự chạy tour
// nữa, và cả hai nút "?" (header desktop + nút nổi mobile) tự ẩn vì chúng đều
// gác theo `hasActiveTour`. Bật lại chỉ cần đổi cờ này về `true` — mọi
// `useRegisterTour` và `lib/tour-steps.tsx` vẫn còn nguyên.
const TOUR_ENABLED = false;

type TourContextValue = {
  /** Đăng ký steps cho trang hiện tại. */
  registerTour: (id: TourId, steps: Step[]) => void;
  /** Hủy đăng ký khi rời trang. */
  unregisterTour: (id: TourId) => void;
  /** Chạy lại tour của trang hiện tại (dùng cho nút "Hướng dẫn"). */
  startTour: () => void;
  /** Có tour nào đang đăng ký cho trang hiện tại không. */
  hasActiveTour: boolean;
  /** Tour đang hiển thị; modal khác dùng cờ này để không chồng overlay. */
  isTourRunning: boolean;
  /**
   * Tour đang chạy HOẶC sắp tự bật (đã lên lịch auto-start, chưa kết thúc).
   * Bật NGAY khi trang đăng ký tour cho user mới — không đợi tới lúc tour hiện —
   * để modal khác (vd PWA onboarding) chờ hẳn tour xong rồi mới hiện, tránh
   * chồng nhau do lệch timer.
   */
  isTourPending: boolean;
  /** Nội bộ: `useRegisterTour` gọi khi đã lên lịch auto-start cho user mới. */
  markAutoStartPending: () => void;
  /** Chặn tour trong webview vì popup mở trình duyệt ngoài phải luôn nằm một mình. */
  isTourBlocked: boolean;
};

const TourContext = createContext<TourContextValue | null>(null);

export function TourProvider({ children }: { children: ReactNode }) {
  const [isMounted, setIsMounted] = useState(false);
  const [tourId, setTourId] = useState<TourId | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);
  const [run, setRun] = useState(false);
  /** Tour đã lên lịch tự bật nhưng chưa kết thúc (gồm cả khoảng chờ 900ms trước khi hiện). */
  const [autoStartPending, setAutoStartPending] = useState(false);
  // Chặn mặc định trong lượt render đầu tiên để tour không kịp lên lịch trước khi
  // navigator.userAgent được kiểm tra ở client.
  const [isTourBlocked, setIsTourBlocked] = useState(true);
  /** Đổi key để remount TourHost, đảm bảo tour chạy lại từ bước đầu. */
  const [runKey, setRunKey] = useState(0);
  const tourIdRef = useRef<TourId | null>(null);

  useEffect(() => {
    setIsMounted(true);
    setIsTourBlocked(isInAppBrowser());
  }, []);

  const registerTour = useCallback((id: TourId, nextSteps: Step[]) => {
    tourIdRef.current = id;
    setTourId(id);
    setSteps(nextSteps);
  }, []);

  const unregisterTour = useCallback((id: TourId) => {
    if (tourIdRef.current !== id) return;
    tourIdRef.current = null;
    setTourId(null);
    setSteps([]);
    setRun(false);
    setAutoStartPending(false);
  }, []);

  const startTour = useCallback(() => {
    if (!TOUR_ENABLED || isTourBlocked || !steps.length) return;
    setRunKey((k) => k + 1);
    setRun(true);
  }, [isTourBlocked, steps.length]);

  const markAutoStartPending = useCallback(() => setAutoStartPending(true), []);

  const handleFinish = useCallback(() => {
    const id = tourIdRef.current;
    if (id) markTourSeen(id);
    setRun(false);
    setAutoStartPending(false);
  }, []);

  const value = useMemo<TourContextValue>(
    () => ({
      registerTour,
      unregisterTour,
      startTour,
      markAutoStartPending,
      hasActiveTour: TOUR_ENABLED && !isTourBlocked && steps.length > 0,
      isTourRunning: run,
      isTourPending: TOUR_ENABLED && !isTourBlocked && (run || autoStartPending),
      isTourBlocked,
    }),
    [
      registerTour,
      unregisterTour,
      startTour,
      markAutoStartPending,
      steps.length,
      run,
      autoStartPending,
      isTourBlocked,
    ],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {isMounted && tourId && steps.length > 0 && (
        <Suspense fallback={null}>
          <TourHost key={runKey} tourId={tourId} steps={steps} run={run} onFinish={handleFinish} />
        </Suspense>
      )}
    </TourContext.Provider>
  );
}

export function useTour(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) {
    throw new Error("useTour phải được dùng bên trong <TourProvider>");
  }
  return ctx;
}

/**
 * Hook để một trang đăng ký tour của mình.
 *
 * @param id        Định danh tour (cũng là khóa localStorage).
 * @param ready     Đã sẵn sàng để (có thể) tự bật tour chưa — thường là `isMounted`
 *                  và/hoặc user đã load xong. Tránh bật khi DOM chưa có target.
 * @param autoStart Tự bật cho user mới (mặc định true).
 */
export function useRegisterTour(id: TourId, ready: boolean, autoStart = true): void {
  const { registerTour, unregisterTour, startTour, markAutoStartPending, isTourBlocked } =
    useTour();
  const steps = TOUR_STEPS[id];
  /** Tour id đã tự bật gần nhất — đổi id (ví dụ sang giai đoạn đọc quẻ) sẽ cho phép tự bật lại. */
  const autoStartedIdRef = useRef<TourId | null>(null);

  useEffect(() => {
    registerTour(id, steps);
    return () => unregisterTour(id);
  }, [id, steps, registerTour, unregisterTour]);

  useEffect(() => {
    if (!TOUR_ENABLED) return;
    if (isTourBlocked) return;
    if (!ready || !autoStart) return;
    if (!steps?.length) return;
    if (autoStartedIdRef.current === id) return;
    if (hasSeenTour(id)) return;
    autoStartedIdRef.current = id;
    // Báo "pending" NGAY để modal khác (PWA onboarding) chờ tour xong mới hiện —
    // không đợi tới mốc 900ms lúc tour thực sự xuất hiện, tránh lệch timer gây chồng.
    markAutoStartPending();
    // Chỉ tự bật ĐÚNG 1 LẦN: đánh dấu đã xem ngay khi auto-start, nên dù user
    // đóng giữa chừng / refresh / rời trang cũng không tự hiện lại.
    markTourSeen(id);
    const timer = window.setTimeout(() => startTour(), AUTO_START_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [id, ready, autoStart, steps?.length, startTour, markAutoStartPending, isTourBlocked]);
}
